import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { Plus, Edit, Trash2, Save, X, BookOpen, Users } from 'lucide-react';

const LevelManagement = () => {
    const [levels, setLevels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingLevel, setEditingLevel] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        min_age: '',
        max_age: '',
        display_order: '',
        badge_name: '',
        badge_icon: '🔬'
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchLevels();
    }, []);

    const fetchLevels = async () => {
        try {
            setLoading(true);
            const response = await adminService.getLevels();
            setLevels(response.levels);
        } catch (err) {
            alert('Failed to fetch levels: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (level = null) => {
        if (level) {
            setEditingLevel(level);
            setFormData({
                name: level.name,
                description: level.description || '',
                min_age: level.min_age,
                max_age: level.max_age,
                display_order: level.display_order,
                badge_name: level.badge_name || '',
                badge_icon: level.badge_icon || '🔬'
            });
        } else {
            setEditingLevel(null);
            setFormData({
                name: '',
                description: '',
                min_age: '',
                max_age: '',
                display_order: levels.length + 1,
                badge_name: '',
                badge_icon: '🔬'
            });
        }
        setErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingLevel(null);
        setFormData({
            name: '',
            description: '',
            min_age: '',
            max_age: '',
            display_order: '',
            badge_name: '',
            badge_icon: '🔬'
        });
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Level name is required';
        }

        if (!formData.min_age || formData.min_age < 1) {
            newErrors.min_age = 'Valid minimum age is required';
        }

        if (!formData.max_age || formData.max_age < formData.min_age) {
            newErrors.max_age = 'Maximum age must be greater than minimum age';
        }

        if (!formData.display_order || formData.display_order < 1) {
            newErrors.display_order = 'Display order is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validate()) return;

        try {
            setLoading(true);

            if (editingLevel) {
                await adminService.updateLevel(editingLevel.id, formData);
                alert('Level updated successfully!');
            } else {
                await adminService.createLevel(formData);
                alert('Level created successfully!');
            }

            handleCloseModal();
            fetchLevels();
        } catch (err) {
            alert('Failed to save level: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (levelId, levelName) => {
        if (!window.confirm(`Are you sure you want to delete "${levelName}"? This will affect all associated terms and lessons.`)) {
            return;
        }

        try {
            setLoading(true);
            await adminService.deleteLevel(levelId);
            alert('Level deleted successfully!');
            fetchLevels();
        } catch (err) {
            alert('Failed to delete level: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const badgeIcons = ['🔬', '🌟', '⚗️', '🏆', '🎓', '💎', '🚀', '⭐', '🌈', '🎯'];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Level Management</h2>
                    <p className="text-gray-600">Manage age-based learning levels</p>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="btn-primary flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    Add Level
                </button>
            </div>

            {/* Levels List */}
            <div className="space-y-4">
                {levels.length === 0 ? (
                    <div className="card text-center py-12">
                        <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No levels created yet</p>
                        <button
                            onClick={() => handleOpenModal()}
                            className="btn-primary mt-4"
                        >
                            Create First Level
                        </button>
                    </div>
                ) : (
                    levels.map(level => (
                        <div key={level.id} className="card hover:shadow-lg transition-all">
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-4 flex-1">
                                    <div className="text-4xl">{level.badge_icon || '🔬'}</div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-bold text-gray-900 mb-1">
                                            {level.name}
                                        </h3>
                                        {level.description && (
                                            <p className="text-gray-600 mb-3">{level.description}</p>
                                        )}
                                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        Age {level.min_age}-{level.max_age}
                      </span>
                                            <span>•</span>
                                            <span>{level.terms_count || 0} Terms</span>
                                            <span>•</span>
                                            <span>{level.lessons_count || 0} Lessons</span>
                                            <span>•</span>
                                            <span className="font-medium text-purple-600">
                        {level.students_count || 0} Students
                      </span>
                                        </div>
                                        {level.badge_name && (
                                            <div className="mt-2">
                        <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
                          🏆 Badge: {level.badge_name}
                        </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 ml-4">
                  <span className="text-sm font-semibold text-gray-500">
                    Order: {level.display_order}
                  </span>
                                    <button
                                        onClick={() => handleOpenModal(level)}
                                        className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                                    >
                                        <Edit className="w-5 h-5 text-blue-600" />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(level.id, level.name)}
                                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="w-5 h-5 text-red-600" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
                            <h2 className="text-2xl font-bold">
                                {editingLevel ? 'Edit Level' : 'Create New Level'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {/* Level Name */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Level Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={`input-field ${errors.name ? 'border-red-500' : ''}`}
                                    placeholder="e.g., Foundation Explorer"
                                />
                                {errors.name && (
                                    <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                                )}
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Description
                                </label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="3"
                                    className="input-field"
                                    placeholder="Brief description of this level..."
                                />
                            </div>

                            {/* Age Range */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Minimum Age <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="min_age"
                                        value={formData.min_age}
                                        onChange={handleChange}
                                        min="1"
                                        max="18"
                                        className={`input-field ${errors.min_age ? 'border-red-500' : ''}`}
                                        placeholder="5"
                                    />
                                    {errors.min_age && (
                                        <p className="text-red-600 text-sm mt-1">{errors.min_age}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        Maximum Age <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="max_age"
                                        value={formData.max_age}
                                        onChange={handleChange}
                                        min="1"
                                        max="18"
                                        className={`input-field ${errors.max_age ? 'border-red-500' : ''}`}
                                        placeholder="6"
                                    />
                                    {errors.max_age && (
                                        <p className="text-red-600 text-sm mt-1">{errors.max_age}</p>
                                    )}
                                </div>
                            </div>

                            {/* Display Order */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Display Order <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    name="display_order"
                                    value={formData.display_order}
                                    onChange={handleChange}
                                    min="1"
                                    className={`input-field ${errors.display_order ? 'border-red-500' : ''}`}
                                    placeholder="1"
                                />
                                {errors.display_order && (
                                    <p className="text-red-600 text-sm mt-1">{errors.display_order}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">
                                    Determines the order levels are displayed (1 = first)
                                </p>
                            </div>

                            {/* Badge Name */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Badge Name
                                </label>
                                <input
                                    type="text"
                                    name="badge_name"
                                    value={formData.badge_name}
                                    onChange={handleChange}
                                    className="input-field"
                                    placeholder="e.g., Junior Scientist"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Badge awarded when student completes this level
                                </p>
                            </div>

                            {/* Badge Icon */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    Badge Icon
                                </label>
                                <div className="flex gap-2 mb-2">
                                    {badgeIcons.map(icon => (
                                        <button
                                            key={icon}
                                            type="button"
                                            onClick={() => setFormData(prev => ({ ...prev, badge_icon: icon }))}
                                            className={`text-3xl p-2 rounded-lg border-2 transition-all ${
                                                formData.badge_icon === icon
                                                    ? 'border-purple-500 bg-purple-50'
                                                    : 'border-gray-200 hover:border-purple-300'
                                            }`}
                                        >
                                            {icon}
                                        </button>
                                    ))}
                                </div>
                                <input
                                    type="text"
                                    name="badge_icon"
                                    value={formData.badge_icon}
                                    onChange={handleChange}
                                    className="input-field"
                                    placeholder="Or enter custom emoji"
                                />
                            </div>

                            {/* Preview */}
                            <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                                <p className="text-sm font-medium text-purple-900 mb-2">Preview:</p>
                                <div className="flex items-center gap-3">
                                    <span className="text-4xl">{formData.badge_icon}</span>
                                    <div>
                                        <p className="font-bold text-lg">{formData.name || 'Level Name'}</p>
                                        <p className="text-sm text-gray-600">
                                            Age {formData.min_age || '?'}-{formData.max_age || '?'}
                                        </p>
                                        {formData.badge_name && (
                                            <p className="text-xs text-purple-600 mt-1">
                                                🏆 Badge: {formData.badge_name}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={handleCloseModal}
                                    className="flex-1 btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 btn-primary flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            {editingLevel ? 'Update Level' : 'Create Level'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LevelManagement;